import React, { useState, useEffect } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import Sidebar from './Sidebar';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAppSelector } from '../hooks/useAppSelector';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { getCurrentQuarterYear } from '../store/slices/complianceObligationsSlice';
import { fetchComplianceSettings } from '../store/slices/complianceSettingsSlice';
import { PageProps } from '../types';
import '../pages/new_risk_management/index.css';

interface NewRiskManagementLayoutProps {
  children: React.ReactNode;
  pages: PageProps[];
}

const NewRiskManagementLayout: React.FC<NewRiskManagementLayoutProps> = ({ children, pages }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isSidebarOpen, setIsSidebarOpen] = useState(!isMobile);
  const [activePageTitle, setActivePageTitle] = useState('New Risk Management');
  const location = useLocation();
  const { user } = useAuth();
  const obligations = useAppSelector((state: any) => state.complianceObligations.obligations);
  const dispatch = useAppDispatch();
  const { year: currentYear, quarter: currentQuarter } = useAppSelector(getCurrentQuarterYear);
  const settingsStatus = useAppSelector((state: any) => state.complianceSettings.status);
  let { quarterlyBadge, reviewBadge } = useAppSelector((state) => state.complianceObligations);

  // Handle compliance badges
  if (user?.isComplianceChampion && currentYear && currentQuarter) {
    quarterlyBadge = obligations.filter((ob: any) => {
      if (ob.status !== 'Active') return false;
      const update = ob.update?.find((u: any) => u.year === currentYear.toString() && u.quarter === currentQuarter);
      return !update || (update.assessmentStatus !== 'Submitted' && update.assessmentStatus !== 'Approved');
    }).length;
  }
  if (user?.isComplianceSuperUser && currentYear && currentQuarter) {
    reviewBadge = obligations.filter((ob: any) => {
      if (ob.status !== 'Active') return false;
      const update = ob.update?.find((u: any) => u.year === currentYear.toString() && u.quarter === currentQuarter);
      return update && update.assessmentStatus === 'Submitted';
    }).length;
  }

  useEffect(() => {
    if (settingsStatus === 'idle') {
      dispatch(fetchComplianceSettings());
    }
  }, [dispatch, settingsStatus]);

  // Handle sidebar state on screen resize
  useEffect(() => {
    setIsSidebarOpen(!isMobile);
  }, [isMobile]);

  const handlePageChange = (title: string) => {
    setActivePageTitle(title);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const pagePropsList: PageProps[] = pages.map((page) => ({
    title: page.title,
    icon: page.icon || null,
    tabs: page.tabs,
    path: page.path,
    show: page.show,
    element: page.element
  }));

  return (
    <div className="risk-management-module">
      <Box sx={{
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
        bgcolor: '#F9FAFB'
      }}>
        <Sidebar
          isOpen={isSidebarOpen}
          onToggle={toggleSidebar}
          activePageTitle={activePageTitle}
          onPageChange={handlePageChange}
          pagePropsList={pagePropsList}
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            transition: theme.transitions.create('margin', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            marginLeft: isSidebarOpen ? '310px' : '64px',
            height: '100vh',
            overflow: 'auto',
            position: 'relative',
            '@media (max-width: 600px)': {
              marginLeft: isSidebarOpen ? '0' : '64px',
              width: '100%',
            },
          }}
        >
          {children}
        </Box>
      </Box>
    </div>
  );
};

export default NewRiskManagementLayout;
